import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/AllTracks.css';

const AllTracks = () => {
  const { handleTrackClick } = useContext(AudioContext);
  const [allTracks, setAllTracks] = useState([]);
  const [userData, setUserData] = useState({
    artistName: '',
    profilePicture: '',
    bio: '',
  });
  const [loading, setLoading] = useState(true);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const firebaseUid = auth.currentUser?.uid;
        if (!firebaseUid) {
          navigate('/login');
          return;
        }

        // Fetch user data
        const userResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/users/firebase/${firebaseUid}`
        );
        setUserData(userResponse.data);

        // Fetch pinned tracks
        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned`,
          { params: { firebaseUid } }
        );
        setPinnedTracks(pinnedResponse.data);

        // Fetch uploads
        const uploadsResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads`,
          { params: { firebaseUid } }
        );

        // Sort uploads by uploadedAt date
        const sortedTracks = uploadsResponse.data.sort(
          (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
        );
        setAllTracks(sortedTracks);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error.response ? error.response.data : error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [auth, navigate]);

  const handleDeleteTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (window.confirm('Are you sure you want to delete this track?')) {
      try {
        await axios.delete('https://sync-api-troz.onrender.com/api/uploads/delete-track', {
          data: { trackId, firebaseUid },
        });
        window.alert('Track deleted successfully');
        setAllTracks((prevTracks) => prevTracks.filter((track) => track._id !== trackId));
      } catch (error) {
        console.error('Error deleting track:', error);
        window.alert('Failed to delete track');
      }
    }
  };

  const handleLikeTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/like', {
        trackId,
        firebaseUid,
      });
      setAllTracks((prevTracks) =>
        prevTracks.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), firebaseUid] }
            : track
        )
      );
    } catch (error) {
      console.error('Error liking track:', error);
    }
  };

  const handlePinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/pin', {
        firebaseUid,
        trackId,
      });
      const pinnedResponse = await axios.get(
        `https://sync-api-troz.onrender.com/api/uploads/pinned`,
        { params: { firebaseUid } }
      );
      setPinnedTracks(pinnedResponse.data);
    } catch (error) {
      console.error('Error pinning track:', error);
    }
  };

  const handleUnpinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/unpin', {
        firebaseUid,
        trackId,
      });
      setPinnedTracks((prev) => prev.filter((track) => track._id !== trackId));
    } catch (error) {
      console.error('Error unpinning track:', error);
    }
  };

  if (loading) {
    return (
      <div className="all-tracks-loading">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="all-tracks-container">
      <div className="profile-banner">
        {userData.profilePicture ? (
          <img src={userData.profilePicture} alt="Banner" className="banner-image" />
        ) : (
          <div className="banner-placeholder" />
        )}
        <p className="artist-name">{userData.artistName}</p>
      </div>
      <div className="scroll-content">
        <div className="header">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft size={24} color="#fff" />
          </button>
          <p className="header-text">All Tracks</p>
          <div className="placeholder" />
        </div>
        {allTracks.length > 0 ? (
          allTracks.map((track) => (
            <TrackItem
              key={track._id}
              track={track}
              onPlay={() => handleTrackClick(track)}
              onPin={() => handlePinTrack(track._id)}
              onUnpin={() => handleUnpinTrack(track._id)}
              isPinned={pinnedTracks.some((t) => t._id === track._id)}
              onDelete={() => handleDeleteTrack(track._id)}
              onLike={() => handleLikeTrack(track._id)}
              currentUserId={auth.currentUser?.uid}
            />
          ))
        ) : (
          <p className="no-tracks-text">You don't have any tracks yet</p>
        )}
      </div>
    </div>
  );
};

export default AllTracks;